import { useEffect } from 'react';
import { onCLS, onFCP, onLCP, onTTFB, onINP } from 'web-vitals';

interface PerformanceMetrics {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

// Declare gtag for TypeScript
declare global {
  function gtag(...args: any[]): void;
}

const PerformanceMonitor = () => {
  useEffect(() => {
    // Function to send metrics to analytics
    const sendToAnalytics = (metric: PerformanceMetrics) => {
      // In production, you would send this to your analytics service
      console.log('Performance Metric:', {
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });

      // Example: Send to Google Analytics 4
      if (typeof gtag !== 'undefined') {
        gtag('event', metric.name, {
          event_category: 'Web Vitals',
          event_label: metric.id,
          value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          custom_map: {
            metric_rating: metric.rating,
            metric_delta: metric.delta,
          },
        });
      }

      // Example: Send to custom analytics endpoint
      if (process.env.NODE_ENV === 'production') {
        fetch('/api/analytics/web-vitals', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            metric: metric.name,
            value: metric.value,
            rating: metric.rating,
            delta: metric.delta,
            id: metric.id,
            url: window.location.href,
            timestamp: Date.now(),
          }),
        }).catch(console.error);
      }
    };

    // Measure Core Web Vitals
    onCLS(sendToAnalytics);
    onINP(sendToAnalytics); // INP replaces FID
    onFCP(sendToAnalytics);
    onLCP(sendToAnalytics);
    onTTFB(sendToAnalytics);

    // Additional performance monitoring
    const measureCustomMetrics = () => {
      // Measure Time to Interactive (TTI) approximation
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          const navigationStart = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (navigationStart) {
            const tti = performance.now();
            console.log('Time to Interactive (approx):', tti);
            
            // Send TTI metric
            sendToAnalytics({
              name: 'TTI',
              value: tti,
              rating: tti < 3800 ? 'good' : tti < 7300 ? 'needs-improvement' : 'poor',
              delta: 0,
              id: 'tti-' + Math.random().toString(36).substr(2, 9),
            });
          }
        });
      }

      // Measure resource loading performance
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const slowResources = resources.filter(resource => resource.duration > 1000);
      
      if (slowResources.length > 0) {
        console.log('Slow loading resources:', slowResources);
        slowResources.forEach(resource => {
          sendToAnalytics({
            name: 'SLOW_RESOURCE',
            value: resource.duration,
            rating: 'poor',
            delta: 0,
            id: resource.name,
          });
        });
      }

      // Measure JavaScript bundle size impact
      const jsResources = resources.filter(resource => 
        resource.name.includes('.js') && resource.transferSize
      );
      
      const totalJSSize = jsResources.reduce((total, resource) => 
        total + (resource.transferSize || 0), 0
      );

      if (totalJSSize > 0) {
        console.log('Total JavaScript size:', totalJSSize, 'bytes');
        sendToAnalytics({
          name: 'JS_BUNDLE_SIZE',
          value: totalJSSize,
          rating: totalJSSize < 200000 ? 'good' : totalJSSize < 500000 ? 'needs-improvement' : 'poor',
          delta: 0,
          id: 'js-bundle-size',
        });
      }
    };

    // Run custom metrics after page load
    if (document.readyState === 'complete') {
      measureCustomMetrics();
    } else {
      window.addEventListener('load', measureCustomMetrics);
    }

    // Monitor memory usage (if available)
    if ('memory' in performance) {
      const memoryInfo = (performance as any).memory;
      console.log('Memory usage:', {
        used: memoryInfo.usedJSHeapSize,
        total: memoryInfo.totalJSHeapSize,
        limit: memoryInfo.jsHeapSizeLimit,
      });

      // Alert if memory usage is high
      const memoryUsageRatio = memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit;
      if (memoryUsageRatio > 0.8) {
        sendToAnalytics({
          name: 'HIGH_MEMORY_USAGE',
          value: memoryUsageRatio * 100,
          rating: 'poor',
          delta: 0,
          id: 'memory-usage',
        });
      }
    }

    // Monitor long tasks (if available)
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              console.log('Long task detected:', entry);
              sendToAnalytics({
                name: 'LONG_TASK',
                value: entry.duration,
                rating: entry.duration < 50 ? 'good' : entry.duration < 100 ? 'needs-improvement' : 'poor',
                delta: 0,
                id: 'long-task-' + entry.startTime,
              });
            }
          }
        });

        longTaskObserver.observe({ entryTypes: ['longtask'] });

        return () => {
          longTaskObserver.disconnect();
        };
      } catch (error) {
        console.warn('Long task observer not supported:', error);
      }
    }
  }, []);

  // This component doesn't render anything
  return null;
};

export default PerformanceMonitor;
